import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AdvShell } from "@/components/AdvShell";
import { StatusBadge, money, num } from "./advUtils";
import { useTakeoverStatus, TakeoverBadge, TakeoverSummary } from "@/components/TakeoverBadge";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Pause, Play, Pencil, BarChart3, Send } from "lucide-react";

const FILTERS = [["all", "All"], ["draft", "Draft"], ["pending_review", "Pending"], ["scheduled", "Scheduled"], ["active", "Active"], ["completed", "Completed"]];
const src = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);

export default function AdvCampaigns() {
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const takeover = useTakeoverStatus(rows, "id");
  const load = () => api.get(`/adv/campaigns?status=${filter}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const submit = (c) => act(() => api.post(`/adv/campaigns/${c.id}/submit`), "Submitted for review");
  const del = (c) => act(() => api.delete(`/adv/campaigns/${c.id}`), "Draft deleted");
  const pause = (c) => act(() => api.post(`/adv/campaigns/${c.id}/pause`), "Paused");
  const resume = (c) => act(() => api.post(`/adv/campaigns/${c.id}/resume`), "Resumed");
  const dup = async (c) => { try { const { data } = await api.post(`/adv/campaigns/${c.id}/duplicate`); toast.success("Duplicated as draft"); nav(`/advertiser/campaigns/${data.id}/edit`); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const actions = (c) => {
    const btn = "inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-race-line hover:border-race-volt hover:text-race-volt transition-colors";
    const A = [];
    if (c.status === "draft") { A.push(<Link key="e" to={`/advertiser/campaigns/${c.id}/edit`} className={btn} data-testid={`adv-edit-${c.id}`}><Pencil size={12} /> Edit</Link>); A.push(<button key="s" onClick={() => submit(c)} className={btn} data-testid={`adv-submit-${c.id}`}><Send size={12} /> Submit</button>); A.push(<button key="d" onClick={() => del(c)} className={`${btn} hover:!text-race-red hover:!border-race-red/50`} data-testid={`adv-delete-${c.id}`}><Trash2 size={12} /> Delete</button>); }
    else if (c.status === "changes_requested") { A.push(<Link key="e" to={`/advertiser/campaigns/${c.id}/edit`} className={btn}><Pencil size={12} /> Edit</Link>); A.push(<button key="s" onClick={() => submit(c)} className={btn}><Send size={12} /> Resubmit</button>); }
    else if (c.status === "active") { A.push(<Link key="a" to={`/advertiser/analytics?c=${c.id}`} className={btn}><BarChart3 size={12} /> Analytics</Link>); A.push(<button key="p" onClick={() => pause(c)} className={btn}><Pause size={12} /> Pause</button>); }
    else if (c.status === "paused") { A.push(<button key="r" onClick={() => resume(c)} className={btn}><Play size={12} /> Resume</button>); }
    else if (c.status === "completed") { A.push(<Link key="a" to={`/advertiser/analytics?c=${c.id}`} className={btn}><BarChart3 size={12} /> Report</Link>); A.push(<button key="d" onClick={() => dup(c)} className={btn}><Copy size={12} /> Duplicate</button>); }
    else { A.push(<Link key="v" to={`/advertiser/campaigns/${c.id}`} className={btn}>View</Link>); }
    return A;
  };

  return (
    <AdvShell>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-4xl tracking-tight" data-testid="adv-campaigns-title">My Campaigns</h1>
        <Link to="/advertiser/campaigns/new" className="inline-flex items-center gap-2 bg-race-volt text-race-bg font-semibold text-sm px-4 py-2.5 rounded-md hover:opacity-90"><Plus size={16} /> Create Campaign</Link>
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`adv-filter-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${filter === k ? "bg-race-txt text-race-bg font-semibold" : "text-race-sub border border-race-line hover:text-race-txt"}`}>{l}</button>
        ))}
      </div>

      <div className="mb-4"><TakeoverSummary count={takeover.badCount} /></div>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-race-line py-16 text-center text-race-sub" data-testid="adv-campaigns-empty">No campaigns in this view.</div>
      ) : (
        <div className="space-y-3" data-testid="adv-campaigns-list">
          {rows.map((c) => (
            <div key={c.id} className="bg-race-surface border border-race-line rounded-lg p-4" data-testid={`adv-camp-row-${c.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-28 h-14 rounded bg-race-bg border border-race-line overflow-hidden shrink-0">
                  {c.image_url && <img src={src(c.image_url)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Link to={`/advertiser/campaigns/${c.id}`} className="font-semibold hover:text-race-volt truncate">{c.name}</Link>
                    <StatusBadge status={c.status} label={c.status_label} />
                    <TakeoverBadge st={takeover.get(c)} id={c.id}
                      onReplace={["draft", "changes_requested"].includes(c.status) ? () => nav(`/advertiser/campaigns/${c.id}/edit?step=creative`) : null}
                      hint={["draft", "changes_requested"].includes(c.status) ? null : (c.status === "completed" ? "Duplicate this campaign to upload a new one." : "Pause it and contact IBRA support to swap the image.")} />
                  </div>
                  <div className="text-xs text-race-sub mt-1">
                    {c.start_date ? `${c.start_date.slice(0, 10)} → ${(c.end_date || "").slice(0, 10)}` : "Dates not set"} · Budget {money(c.budget_usd)}
                  </div>
                  <div className="flex gap-5 mt-2 text-xs text-race-sub">
                    <span>Spend <b className="text-race-txt">{money(c.spend_usd)}</b></span>
                    <span>Impr <b className="text-race-txt">{num(c.impressions)}</b></span>
                    <span>Clicks <b className="text-race-txt">{num(c.clicks)}</b></span>
                    <span>CTR <b className="text-race-txt">{c.ctr}%</b></span>
                  </div>
                </div>
              </div>
              {c.review_message && (c.status === "changes_requested" || c.status === "rejected") && (
                <div className="mt-3 text-xs rounded-md border border-orange-500/30 bg-orange-500/10 text-orange-200 px-3 py-2" data-testid={`adv-review-msg-${c.id}`}>
                  <b>Message from review:</b> {c.review_message}
                </div>
              )}
              <div className="flex gap-2 mt-3 flex-wrap">{actions(c)}</div>
            </div>
          ))}
        </div>
      )}
    </AdvShell>
  );
}
